package com.ragnorak.clanybackoffice.ui.screens

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.clany.backoffice.domain.usecase.GetSubscriptionPlansUseCase
import com.clany.backoffice.domain.usecase.RefreshSubscriptionPlansUseCase
import com.clany.backoffice.domain.usecase.UpdateSubscriptionPlanUseCase
import com.clany.backoffice.model.SubscriptionPlanResponse
import dev.zacsweers.metro.Inject
import kotlinx.coroutines.launch

data class SubscriptionPlansUiState(
    val isLoading: Boolean = false,
    val plans: List<SubscriptionPlanResponse> = emptyList(),
    val error: String? = null,
    val page: Int = 1,
    val hasMore: Boolean = true,
    val searchQuery: String = "",
    val isRefreshing: Boolean = false,
    val selectedPlan: SubscriptionPlanResponse? = null,
    val isSaving: Boolean = false,
    val saveSuccess: Boolean = false
)

class SubscriptionPlansViewModel @Inject constructor(
    private val getSubscriptionPlansUseCase: GetSubscriptionPlansUseCase,
    private val refreshSubscriptionPlansUseCase: RefreshSubscriptionPlansUseCase,
    private val updateSubscriptionPlanUseCase: UpdateSubscriptionPlanUseCase
) : ViewModel() {

    var uiState by mutableStateOf(SubscriptionPlansUiState())
        private set

    init {
        loadPlans()
    }

    fun loadPlans(page: Int = 1) {
        viewModelScope.launch {
            uiState = uiState.copy(isLoading = true, error = null)
            getSubscriptionPlansUseCase(
                page = page,
                pageSize = 20,
                query = uiState.searchQuery.ifBlank { null }
            )
                .onSuccess { plans ->
                    uiState = uiState.copy(
                        isLoading = false,
                        plans = if (page == 1) plans else uiState.plans + plans,
                        page = page,
                        hasMore = plans.isNotEmpty()
                    )
                }
                .onFailure {
                    uiState = uiState.copy(isLoading = false, error = it.message)
                }
        }
    }

    fun onSearchQueryChanged(query: String) {
        uiState = uiState.copy(searchQuery = query)
        loadPlans(page = 1)
    }

    fun refreshPlans() {
        viewModelScope.launch {
            uiState = uiState.copy(isRefreshing = true, error = null)
            refreshSubscriptionPlansUseCase()
                .onSuccess {
                    uiState = uiState.copy(isRefreshing = false)
                    loadPlans(page = 1)
                }
                .onFailure {
                    uiState = uiState.copy(isRefreshing = false, error = "Refresh failed: ${it.message}")
                }
        }
    }

    fun selectPlan(plan: SubscriptionPlanResponse) {
        uiState = uiState.copy(selectedPlan = plan, saveSuccess = false)
    }

    fun dismissPlan() {
        uiState = uiState.copy(selectedPlan = null, saveSuccess = false)
    }

    fun updatePlan(plan: SubscriptionPlanResponse) {
        viewModelScope.launch {
            uiState = uiState.copy(isSaving = true, error = null, saveSuccess = false)
            updateSubscriptionPlanUseCase(plan.id, plan)
                .onSuccess {
                    uiState = uiState.copy(isSaving = false, saveSuccess = true, selectedPlan = null)
                    loadPlans(page = 1)
                }
                .onFailure {
                    uiState = uiState.copy(isSaving = false, error = "Save failed: ${it.message}")
                }
        }
    }
}
